import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Search, Filter, FileText, Calendar, RefreshCw, Send, Trash2, Percent } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';
import InvoiceModal from '../components/InvoiceModal';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const downloadPdf = async (url, filename) => {
  try {
    const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename || 'document.pdf');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
  } catch (e) { toast.error('Failed to download PDF'); }
};

export default function BillLog() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [shareBill, setShareBill] = useState(null);  // opens InvoiceModal (WhatsApp / Email / Twilio)
  // iter211 — Cash-discount modal state. Opens when the operator clicks the
  // % icon on a bill row. Lets them subtract a settlement discount post-
  // billing (e.g. customer paid immediate cash, asked for a few rupees off).
  const [discountBill, setDiscountBill] = useState(null);
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [discountSubmitting, setDiscountSubmitting] = useState(false);

  const applyDiscount = async () => {
    if (!discountBill) return;
    const amt = parseFloat(discountValue);
    if (!amt || amt <= 0) { toast.error('Enter a positive discount amount'); return; }
    setDiscountSubmitting(true);
    try {
      const res = await axios.patch(
        `${API}/invoices/bill-log/${discountBill.id}/discount`,
        { discount_amount: amt, reason: discountReason },
        { withCredentials: true },
      );
      toast.success(`Discount applied · New total ₹${res.data.new_total.toLocaleString('en-IN')}`);
      setDiscountBill(null); setDiscountValue(''); setDiscountReason('');
      fetchBills();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply discount');
    } finally { setDiscountSubmitting(false); }
  };
  const [dateTo, setDateTo] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('All');
  // Click-to-sort state. Default = invoice number DESC (newest on top per
  // user spec Feb-2026). Clicking the active column toggles asc/desc;
  // clicking a new column resets to desc.
  const [sortKey, setSortKey] = useState('invoice_number');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const fetchBills = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== 'All') params.append('invoice_type', tab);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (clientSearch) params.append('client', clientSearch);
      if (productSearch) params.append('product', productSearch);
      if (paymentFilter !== 'All') params.append('payment_status', paymentFilter);
      const res = await axios.get(`${API}/invoices/bill-log?${params.toString()}`, { withCredentials: true });
      setBills(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Admin-only: 10s undo + confirm modal. Each row deletes via the new
  // /api/invoices/bill-log/{id} endpoint which drops the voucher ledger
  // entry and either removes the placeholder sales_order (voucher-sourced)
  // or strips the invoice linkage from a real Sales Order — see
  // routes/invoices.py::delete_bill_from_log.
  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchBills);

  const cleanGhostBills = async () => {
    if (!window.confirm('Drop sales_orders rows whose linked voucher has already been deleted? This unsticks ghost bills (idempotent — safe to re-run).')) return;
    try {
      const r = await axios.post(`${API}/sales/orders/cleanup-orphaned`, {}, { withCredentials: true });
      toast.success(`Cleanup complete — dropped ${r.data.count} ghost bill(s)${r.data.count > 0 ? ': ' + r.data.dropped.join(', ') : ''}`);
      await fetchBills();
    } catch (e) {
      toast.error('Cleanup failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { fetchBills(); }, [tab, paymentFilter]);

  const applyFilters = () => fetchBills();

  const gstCount = bills.filter(b => b.gst_enabled).length;
  const nonGstCount = bills.filter(b => !b.gst_enabled).length;
  const totalAmount = bills.reduce((s, b) => s + (parseFloat(b.total_amount) || 0), 0);

  // Numeric-aware sort: trailing integer block for invoice numbers so
  // `TI/GST/26-27/100` > `…/099`. Falls back to lexicographic compare for
  // text columns.
  const seq = (s) => parseInt(((s || '').match(/(\d+)$/) || ['0', '0'])[1], 10);
  const cmp = (a, b) => {
    let va, vb;
    switch (sortKey) {
      case 'invoice_number': va = seq(a.invoice_number); vb = seq(b.invoice_number); break;
      case 'type':           va = a.gst_enabled ? 1 : 0; vb = b.gst_enabled ? 1 : 0; break;
      case 'client':         va = (a.client || '').toLowerCase(); vb = (b.client || '').toLowerCase(); break;
      case 'subtotal':       va = parseFloat(a.subtotal || 0); vb = parseFloat(b.subtotal || 0); break;
      case 'gst_amount':     va = parseFloat(a.gst_amount || 0); vb = parseFloat(b.gst_amount || 0); break;
      case 'total_amount':   va = parseFloat(a.total_amount || 0); vb = parseFloat(b.total_amount || 0); break;
      case 'payment_status': va = (a.payment_status || 'Unpaid').toLowerCase(); vb = (b.payment_status || 'Unpaid').toLowerCase(); break;
      case 'date':
      default:               va = a.invoice_date || a.created_at || ''; vb = b.invoice_date || b.created_at || ''; break;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  };
  const sortedBills = [...bills].sort(cmp);
  const paidCount = bills.filter(b => b.payment_status === 'Paid').length;

  return (
    <div className="space-y-5" data-testid="bill-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <FileText size={20} className="text-amber-400" /> Bill Log
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">{bills.length} invoices · {fmt(totalAmount)} total · {paidCount} paid</p>
        </div>
        <button onClick={fetchBills} className="flex items-center gap-1.5 text-zinc-400 hover:text-zinc-200 text-xs border border-zinc-800 px-3 py-2 rounded-xl transition-colors">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Total Invoices</div>
          <div className="font-heading font-bold text-xl text-zinc-100">{bills.length}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider">GST (Tirth Industries)</div>
          <div className="font-heading font-bold text-xl text-green-400">{gstCount}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Non-GST (Enterprises)</div>
          <div className="font-heading font-bold text-xl text-amber-400">{nonGstCount}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider">Total Value</div>
          <div className="font-heading font-bold text-xl text-amber-400">{fmt(totalAmount)}</div>
        </div>
      </div>

      {/* Tabs: All / GST / Non-GST */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg p-0.5 gap-0.5">
          {['All', 'GST', 'Non-GST'].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase().replace('-', '')}`}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${tab === t ? (t === 'GST' ? 'bg-green-600 text-white font-bold' : t === 'Non-GST' ? 'bg-amber-500 text-black font-bold' : 'bg-zinc-700 text-zinc-100 font-bold') : 'text-zinc-500 hover:text-zinc-200'}`}>
              {t} {t === 'GST' ? `(${gstCount})` : t === 'Non-GST' ? `(${nonGstCount})` : ''}
            </button>
          ))}
        </div>

        <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-2.5 py-1.5 text-zinc-400 text-xs focus:outline-none" data-testid="payment-filter">
          {['All', 'Paid', 'Partial', 'Unpaid'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {isAdmin && (
          <button onClick={cleanGhostBills}
            data-testid="bill-log-clean-ghosts"
            title="Drop sales_orders whose voucher is already gone (idempotent)"
            className="ml-auto flex items-center gap-1 text-[10px] px-2.5 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 rounded-lg">
            <Trash2 size={11}/> Clean ghost bills
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-end bg-zinc-900/50 border border-zinc-800 rounded-xl p-3">
        <div>
          <label className="text-zinc-600 text-[10px] mb-1 block">From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none" />
        </div>
        <div>
          <label className="text-zinc-600 text-[10px] mb-1 block">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none" />
        </div>
        <div>
          <label className="text-zinc-600 text-[10px] mb-1 block">Client</label>
          <input value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="Client name..."
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none w-36" />
        </div>
        <div>
          <label className="text-zinc-600 text-[10px] mb-1 block">Product</label>
          <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Product name..."
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-zinc-300 text-xs focus:outline-none w-36" />
        </div>
        <button onClick={applyFilters} data-testid="apply-filters"
          className="flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-1.5 rounded-lg transition-colors">
          <Filter size={11} /> Apply
        </button>
        {(dateFrom || dateTo || clientSearch || productSearch) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); setClientSearch(''); setProductSearch(''); setTimeout(fetchBills, 100); }}
            className="text-zinc-500 hover:text-zinc-300 text-xs underline">Clear</button>
        )}
      </div>

      {/* Bill table */}
      {loading ? (
        <div className="text-zinc-400 text-sm animate-pulse p-8 text-center">Loading bills...</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]" data-testid="bill-log-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  {[
                    { label: 'Invoice No', key: 'invoice_number' },
                    { label: 'Type',       key: 'type' },
                    { label: 'Client',     key: 'client' },
                    { label: 'Items',      key: null },
                    { label: 'Subtotal',   key: 'subtotal' },
                    { label: 'GST',        key: 'gst_amount' },
                    { label: 'Total',      key: 'total_amount' },
                    { label: 'Payment',    key: 'payment_status' },
                    { label: 'Date',       key: 'date' },
                    { label: 'PDF',        key: null },
                  ].map(({ label, key }) => {
                    const active = key && key === sortKey;
                    return (
                      <th key={label}
                        onClick={key ? () => toggleSort(key) : undefined}
                        className={`text-left text-xs font-medium px-4 py-3 select-none ${key ? 'cursor-pointer hover:text-zinc-200' : ''} ${active ? 'text-amber-400' : 'text-zinc-500'}`}
                        data-testid={key ? `bill-log-sort-${key}` : undefined}
                        title={key ? 'Click to sort' : ''}>
                        {label}
                        {active && <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedBills.map(b => {
                  // iter203 — Show item name properly for single-item bills
                  // (especially RM-only bills where b.product_name is empty
                  // but items[0].product_name carries the raw material's name).
                  // Also strike-through cancelled bills.
                  const cancelled = b.status === 'cancelled';
                  const firstItem = (b.items && b.items.length === 1) ? b.items[0] : null;
                  const singleName = firstItem
                    ? (firstItem.product_name || firstItem.material_name || '')
                    : (b.product_name || '');
                  return (
                  <tr key={b.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${cancelled ? 'opacity-60' : ''}`} data-testid={`bill-row-${b.id}`}>
                    <td className="px-4 py-3 font-mono text-amber-400 text-xs font-semibold">
                      <span className={cancelled ? 'line-through decoration-red-500/70' : ''}>{b.invoice_number || b.voucher_no || '—'}</span>
                      {cancelled && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-700/60 align-middle">CANCELLED</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.gst_enabled ? 'bg-green-900/40 text-green-300 border-green-700' : 'bg-amber-900/40 text-amber-300 border-amber-700'}`}>
                        {b.gst_enabled ? 'GST' : 'Non-GST'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-200 text-xs">{b.client}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{b.items?.length > 1 ? `${b.items.length} items` : (singleName || '—')}</td>
                    <td className={`px-4 py-3 font-mono text-zinc-300 text-xs ${cancelled ? 'line-through text-zinc-500' : ''}`}>{fmt(b.subtotal)}</td>
                    <td className="px-4 py-3 font-mono text-zinc-500 text-xs">{b.gst_enabled ? fmt(b.gst_amount) : '—'}</td>
                    <td className={`px-4 py-3 font-mono text-amber-400 text-xs font-semibold ${cancelled ? 'line-through text-zinc-500' : ''}`}>{fmt(b.total_amount)}</td>
                    <td className="px-4 py-3">
                      {cancelled ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-red-900/30 text-red-300 border-red-700">Cancelled</span>
                      ) : (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700' : b.payment_status === 'Partial' ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-red-900/30 text-red-400 border-red-800'}`}>
                          {b.payment_status || 'Unpaid'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{b.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setShareBill(b)}
                          data-testid={`share-btn-${b.id}`}
                          title="Send via WhatsApp / Email / Twilio"
                          className="p-1.5 rounded-lg bg-zinc-800 text-green-400 hover:text-green-300 hover:bg-green-900/20 transition-colors">
                          <Send size={12} />
                        </button>
                        <button onClick={() => downloadPdf(`${API}/invoices/${b.id}/pdf`, `Invoice_${b.invoice_number || b.id}.pdf`)}
                          data-testid={`pdf-btn-${b.id}`}
                          className="p-1.5 rounded-lg bg-zinc-800 text-amber-400 hover:text-amber-300 hover:bg-zinc-700 transition-colors">
                          <Download size={12} />
                        </button>
                        <SubscribeButton refType="invoice" refId={b.id} label={b.invoice_number || b.voucher_no || b.client} size="xs" />
                        {b.payment_status !== 'Paid' && b.status !== 'cancelled' && (
                          <button onClick={() => { setDiscountBill(b); setDiscountValue(''); setDiscountReason(''); }}
                            data-testid={`bill-discount-btn-${b.id}`}
                            title="Apply cash / settlement discount"
                            className="p-1.5 rounded-lg bg-zinc-800 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20 transition-colors">
                            <Percent size={12} />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => requestDelete(
                            `/invoices/bill-log/${b.id}`,
                            b.invoice_number || b.client,
                            `Delete bill ${b.invoice_number || ''}. The underlying voucher will be removed and (if linked) the Sales Order will be reverted to un-billed. 10 seconds to undo.`
                          )}
                            data-testid={`bill-delete-${b.id}`}
                            title="Delete voucher & unlink Sales Order"
                            className="p-1.5 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 hover:bg-red-900/50 border border-red-800/30 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {bills.length === 0 && (
            <div className="text-center py-12 text-zinc-600">
              <FileText size={32} className="mx-auto mb-2 text-zinc-700" />
              <p className="text-xs">No invoices match your filters</p>
            </div>
          )}
        </div>
      )}

      {shareBill && <InvoiceModal order={shareBill} onClose={() => setShareBill(null)} onUpdate={fetchBills} />}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />

      {/* iter211 — Cash / settlement discount modal */}
      {discountBill && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setDiscountBill(null)}>
          <div onClick={e => e.stopPropagation()}
            className="bg-zinc-950 border border-emerald-700/50 rounded-2xl w-full max-w-md shadow-[0_0_40px_rgba(16,185,129,0.18)]"
            data-testid="bill-discount-modal">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
              <Percent size={16} className="text-emerald-400" />
              <h3 className="font-heading font-semibold text-zinc-50">Apply Cash Discount</h3>
            </div>
            <div className="p-5 space-y-3">
              <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-[11px] space-y-1">
                <div className="flex justify-between"><span className="text-zinc-400">Invoice</span><span className="text-zinc-200 font-mono">{discountBill.invoice_number}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Customer</span><span className="text-zinc-200">{discountBill.client}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Current Total</span><span className="text-zinc-200 font-mono">{fmt(discountBill.total_amount)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Paid So Far</span><span className="text-zinc-200 font-mono">{fmt(discountBill.paid_amount || 0)}</span></div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Discount Amount (Rs.)</label>
                <input type="number" min="0" step="0.01" autoFocus
                  value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                  data-testid="bill-discount-input"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-emerald-500"/>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Reason (optional)</label>
                <input type="text" placeholder="Cash payment / round-off / negotiated"
                  value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                  data-testid="bill-discount-reason"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-emerald-500"/>
              </div>
              {parseFloat(discountValue) > 0 && (
                <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-lg p-2 text-[11px] text-emerald-300">
                  New total: <span className="font-mono font-semibold">{fmt((discountBill.total_amount || 0) - parseFloat(discountValue || 0))}</span>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-end gap-2">
              <button onClick={() => setDiscountBill(null)}
                className="px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200">Cancel</button>
              <button onClick={applyDiscount} disabled={discountSubmitting || !discountValue}
                data-testid="bill-discount-apply"
                className="px-4 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg font-semibold transition-colors">
                {discountSubmitting ? 'Applying...' : 'Apply Discount'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
